import { lazy, Suspense, useRef, useState, type CSSProperties } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ImagePlus } from 'lucide-react';
import { uploadRequestInlineImage } from '../../lib/api';
import { useToastStore } from '../../lib/stores';
import { ProxiedImage } from '../ProxiedImage';
import { WorkingSpinner } from '../WorkingSpinner';

// TinyMCE is heavy, so the WYSIWYG editor only loads when the user opens the Rich
// text tab (the raw textarea + preview stay in the request surface's base chunk).
const WysiwygMarkdownEditor = lazy(() =>
  import('../editors/WysiwygMarkdownEditor').then((m) => ({ default: m.WysiwygMarkdownEditor })),
);

/** Stores a request inline image and returns its servable URL (for the WYSIWYG editor). */
const requestImageUpload = (file: File | Blob) => uploadRequestInlineImage(file as File).then((r) => r.url);

type Mode = 'write' | 'wysiwyg' | 'preview';

/** Markdown editor for a request field: toggle between raw Markdown (a textarea),
 *  a WYSIWYG rich-text view, and a rendered Preview. Markdown stays the canonical
 *  value; image upload (paste, drop, or the image button) inserts an `![alt](url)`
 *  link via the request image endpoint. `height` is a fixed editor height in px. */
export function RequestMarkdownEditor({ value, onChange, disabled, placeholder, ariaLabel, height = 480 }: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  placeholder?: string;
  /** Accessible name for the textarea (the visible "Details" label isn't a <label for>). */
  ariaLabel?: string;
  /** Fixed editor height in px (3× the old short default). */
  height?: number;
}) {
  const taRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [mode, setMode] = useState<Mode>('write');
  const [uploading, setUploading] = useState(false);
  const addToast = useToastStore((s) => s.addToast);

  const textareaStyle: CSSProperties = {
    width: '100%', height, fontSize: '14px', padding: 'var(--space-2)',
    borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-subtle)',
    background: 'var(--bg-base)', color: 'var(--text-primary)', resize: 'vertical',
    fontFamily: 'inherit', lineHeight: 1.5,
  };

  const insertAtCursor = (text: string) => {
    const ta = taRef.current;
    if (!ta) { onChange(value + text); return; }
    const start = ta.selectionStart ?? value.length;
    const end = ta.selectionEnd ?? value.length;
    onChange(value.slice(0, start) + text + value.slice(end));
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + text.length;
      ta.setSelectionRange(pos, pos);
    });
  };

  const uploadImages = async (files: File[]) => {
    const images = files.filter((f) => f.type.startsWith('image/'));
    if (!images.length) return;
    setUploading(true);
    // Accumulate all markdown snippets and insert ONCE: `value` is captured in
    // this closure and the component doesn't re-render mid-loop, so inserting per
    // image would overwrite earlier ones with the stale value (dropping all but
    // the last). One insert after the loop keeps every uploaded image.
    const parts: string[] = [];
    for (const f of images) {
      try {
        const { url } = await uploadRequestInlineImage(f);
        const alt = f.name.replace(/\.[^.]+$/, '') || 'image';
        parts.push(`\n![${alt}](${url})\n`);
      } catch (e) {
        addToast({ type: 'error', message: e instanceof Error ? e.message : 'Image upload failed' });
      }
    }
    if (parts.length) insertAtCursor(parts.join(''));
    setUploading(false);
  };

  const tab = (m: Mode, label: string) => (
    <button
      type="button" role="tab" aria-selected={mode === m}
      className={`settings-subnav-chip${mode === m ? ' active' : ''}`}
      onClick={() => setMode(m)}
      disabled={disabled}
    >
      {label}
    </button>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <div className="settings-subnav" role="tablist" aria-label="Editor mode">
          {tab('write', 'Markdown')}
          {tab('wysiwyg', 'Rich text')}
          {tab('preview', 'Preview')}
        </div>
        <span style={{ flex: 1 }} />
        {/* The raw textarea needs the explicit image button; the WYSIWYG view has its
            own toolbar image action, and Preview is read-only. */}
        {mode === 'write' && (
          <button
            type="button" className="btn btn-ghost btn-sm"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            disabled={disabled || uploading}
            onClick={() => fileRef.current?.click()}
            title="Add an image (or paste / drop one into the editor)"
          >
            <ImagePlus size={14} /> {uploading ? 'Uploading…' : 'Image'}
          </button>
        )}
      </div>

      {mode === 'write' && (
        <textarea
          ref={taRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          aria-label={ariaLabel}
          placeholder={placeholder ?? 'Describe the request in Markdown. Paste or drop a screenshot to embed it.'}
          style={textareaStyle}
          onPaste={(e) => {
            const files = Array.from(e.clipboardData?.files ?? []).filter((f) => f.type.startsWith('image/'));
            if (files.length) { e.preventDefault(); void uploadImages(files); }
          }}
          onDragOver={(e) => { if (e.dataTransfer?.types?.includes('Files')) e.preventDefault(); }}
          onDrop={(e) => {
            const files = Array.from(e.dataTransfer?.files ?? []).filter((f) => f.type.startsWith('image/'));
            if (files.length) { e.preventDefault(); void uploadImages(files); }
          }}
        />
      )}

      {mode === 'wysiwyg' && (
        <Suspense fallback={<div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', background: 'var(--bg-base)' }}><WorkingSpinner /></div>}>
          <WysiwygMarkdownEditor
            value={value}
            onChange={onChange}
            imageUpload={requestImageUpload}
            placeholder={placeholder ?? 'Describe the request…'}
            height={height}
          />
        </Suspense>
      )}

      {mode === 'preview' && (
        <div className="markdown-body" style={{ height, overflowY: 'auto', padding: 'var(--space-2)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', background: 'var(--bg-base)' }}>
          {value.trim()
            ? <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ img: ProxiedImage }}>{value}</ReactMarkdown>
            : <span style={{ color: 'var(--text-tertiary)', fontStyle: 'italic' }}>Nothing to preview.</span>}
        </div>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: 'none' }}
        onChange={(e) => { void uploadImages(Array.from(e.target.files ?? [])); e.target.value = ''; }}
      />
    </div>
  );
}
